import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarIcon, Download, Users, Activity, Clock, MapPin, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Doctor, Staff, ScheduleItem, Room } from './SchedulerTypes';

interface NurseDoctorProcedureReportProps {
  scheduleItems: ScheduleItem[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  currentDate: Date;
}

type DateRangeType = 'daily' | 'weekly' | 'monthly' | 'custom';

interface DateRange {
  from: Date;
  to?: Date;
}

interface NurseDoctorStats {
  nurseId: string;
  nurseName: string;
  doctorId: string;
  doctorName: string;
  count: number;
}

interface NurseProcedureStats {
  nurseId: string;
  nurseName: string;
  procedure: string;
  count: number;
}

interface ModalData {
  type: 'nurse-doctor' | 'nurse-procedure';
  nurseId: string;
  nurseName: string;
  doctorId?: string;
  doctorName?: string;
  procedure?: string;
}

interface DetailedInstance {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  roomId?: string;
  roomName?: string;
  doctorName?: string;
}

type SortField = 'nurseName' | 'doctorName' | 'procedure' | 'count';
type SortDirection = 'asc' | 'desc';

const NurseDoctorProcedureReport: React.FC<NurseDoctorProcedureReportProps> = ({
  scheduleItems,
  doctors,
  staff,
  rooms,
  currentDate,
}) => {
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('weekly');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  
  // Handler for calendar range selection
  const handleCustomDateRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (range?.from) {
      setCustomDateRange({ from: range.from, to: range.to });
    } else {
      setCustomDateRange(undefined);
    }
  };
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Sorting and filtering state
  const [nurseDoctorSort, setNurseDoctorSort] = useState<{ field: SortField; direction: SortDirection }>({ field: 'count', direction: 'desc' });
  const [nurseProcedureSort, setNurseProcedureSort] = useState<{ field: SortField; direction: SortDirection }>({ field: 'count', direction: 'desc' });
  const [nurseDoctorSearch, setNurseDoctorSearch] = useState('');
  const [nurseProcedureSearch, setNurseProcedureSearch] = useState('');

  // Calculate date range based on type
  const dateRange = useMemo((): DateRange => {
    switch (dateRangeType) {
      case 'daily':
        return { from: currentDate, to: currentDate };
      case 'weekly':
        return {
          from: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
          to: endOfWeek(currentDate, { weekStartsOn: 1 }),
        };
      case 'monthly':
        return {
          from: startOfMonth(currentDate),
          to: endOfMonth(currentDate),
        };
      case 'custom':
        return (customDateRange && customDateRange.from && customDateRange.to) 
          ? customDateRange 
          : { from: currentDate, to: currentDate };
      default:
        return { from: currentDate, to: currentDate };
    }
  }, [currentDate, dateRangeType, customDateRange]);

  // Filter schedule items by date range and with both doctor and staff assigned
  const filteredScheduleItems = useMemo(() => {
    return scheduleItems.filter(item => {
      // Must be appointment type with both doctor and staff assigned
      if (item.type !== 'appointment' || !item.doctorId || !item.staffId) return false;
      
      // Check if date is within range - use simple string comparison since dates are already in YYYY-MM-DD format
      const itemDate = item.date;
      const rangeFrom = format(dateRange.from, 'yyyy-MM-dd');
      const rangeTo = format(dateRange.to, 'yyyy-MM-dd');
      const withinRange = itemDate >= rangeFrom && itemDate <= rangeTo;
      
      return withinRange;
    });
  }, [scheduleItems, dateRange]);

  // Generate nurse-doctor assistance statistics
  const nurseDoctorStats = useMemo((): NurseDoctorStats[] => {
    const statsMap = new Map<string, NurseDoctorStats>();

    filteredScheduleItems.forEach(item => {
      const nurseId = item.staffId!;
      const doctorId = item.doctorId!;
      const key = `${nurseId}-${doctorId}`;

      const nurse = staff.find(s => s.id === nurseId);
      const doctor = doctors.find(d => d.id === doctorId);

      if (!nurse || !doctor) return;

      if (statsMap.has(key)) {
        statsMap.get(key)!.count++;
      } else {
        statsMap.set(key, {
          nurseId,
          nurseName: nurse.name,
          doctorId,
          doctorName: doctor.name,
          count: 1,
        });
      }
    });

    return Array.from(statsMap.values());
  }, [filteredScheduleItems, staff, doctors]);

  // Filtered and sorted nurse-doctor stats
  const filteredNurseDoctorStats = useMemo((): NurseDoctorStats[] => {
    let filtered = nurseDoctorStats;
    
    // Apply search filter
    if (nurseDoctorSearch) {
      filtered = filtered.filter(stat =>
        stat.nurseName.toLowerCase().includes(nurseDoctorSearch.toLowerCase()) ||
        stat.doctorName.toLowerCase().includes(nurseDoctorSearch.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const { field, direction } = nurseDoctorSort;
      let aValue = a[field];
      let bValue = b[field];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filtered;
  }, [nurseDoctorStats, nurseDoctorSearch, nurseDoctorSort]);

  // Generate nurse-procedure assistance statistics
  const nurseProcedureStats = useMemo((): NurseProcedureStats[] => {
    const statsMap = new Map<string, NurseProcedureStats>();

    filteredScheduleItems.forEach(item => {
      if (!item.title) return;

      const nurseId = item.staffId!;
      const procedure = item.title;
      const key = `${nurseId}-${procedure}`;

      const nurse = staff.find(s => s.id === nurseId);
      if (!nurse) return;

      if (statsMap.has(key)) {
        statsMap.get(key)!.count++;
      } else {
        statsMap.set(key, {
          nurseId,
          nurseName: nurse.name,
          procedure,
          count: 1,
        });
      }
    });

    return Array.from(statsMap.values());
  }, [filteredScheduleItems, staff]);

  // Filtered and sorted nurse-procedure stats
  const filteredNurseProcedureStats = useMemo((): NurseProcedureStats[] => {
    let filtered = nurseProcedureStats;
    
    // Apply search filter
    if (nurseProcedureSearch) {
      filtered = filtered.filter(stat =>
        stat.nurseName.toLowerCase().includes(nurseProcedureSearch.toLowerCase()) ||
        stat.procedure.toLowerCase().includes(nurseProcedureSearch.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const { field, direction } = nurseProcedureSort;
      let aValue = a[field];
      let bValue = b[field];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filtered;
  }, [nurseProcedureStats, nurseProcedureSearch, nurseProcedureSort]);

  // Get detailed instances for modal
  const getDetailedInstances = (modalData: ModalData): DetailedInstance[] => {
    if (!modalData) return [];

    const instances = filteredScheduleItems.filter(item => {
      if (modalData.type === 'nurse-doctor') {
        return item.staffId === modalData.nurseId && item.doctorId === modalData.doctorId;
      } else {
        return item.staffId === modalData.nurseId && item.title === modalData.procedure;
      }
    });

    return instances.map(item => {
      const room = rooms.find(r => r.id === item.roomId);
      const doctor = doctors.find(d => d.id === item.doctorId);
      
      return {
        id: item.id,
        date: item.date,
        startTime: item.startTime,
        endTime: item.endTime,
        title: item.title || '',
        roomId: item.roomId,
        roomName: room?.name,
        doctorName: doctor?.name,
      };
    }).sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const detailedInstances = useMemo(() => {
    if (!modalData) return [];
    return getDetailedInstances(modalData);
  }, [modalData, filteredScheduleItems, rooms, doctors]);

  // Click handlers
  const handleNurseDoctorClick = (stat: NurseDoctorStats) => {
    setModalData({
      type: 'nurse-doctor',
      nurseId: stat.nurseId,
      nurseName: stat.nurseName,
      doctorId: stat.doctorId,
      doctorName: stat.doctorName,
    });
    setIsModalOpen(true);
  };

  const handleNurseProcedureClick = (stat: NurseProcedureStats) => {
    setModalData({
      type: 'nurse-procedure',
      nurseId: stat.nurseId,
      nurseName: stat.nurseName,
      procedure: stat.procedure,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  // Sorting handlers
  const handleNurseDoctorSort = (field: SortField) => {
    setNurseDoctorSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleNurseProcedureSort = (field: SortField) => {
    setNurseProcedureSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort icon helper
  const getSortIcon = (field: SortField, currentSort: { field: SortField; direction: SortDirection }) => {
    if (currentSort.field !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return currentSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const exportToCSV = (data: unknown[], filename: string, headers: string[]) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = (row as Record<string, unknown>)[header];
        return typeof value === 'string'
          ? `"${value}"`
          : String(value ?? '');
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5" />
            <span>Date Range Filter</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">Range:</label>
              <Select value={dateRangeType} onValueChange={(value: DateRangeType) => setDateRangeType(value)}>
                <SelectTrigger className="w-28 sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateRangeType === 'custom' && (
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateRange && customDateRange.from && customDateRange.to
                      ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`
                      : 'Select date range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={setCustomDateRange}
                    numberOfMonths={2}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover>
            )}

            <div className="text-sm text-muted-foreground">
              {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
              <span className="ml-2">({filteredScheduleItems.length} collaborative appointments)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Nurse Assistance Reports</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="nurse-doctor" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="nurse-doctor" className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Nurse-Doctor Assistance</span>
              </TabsTrigger>
              <TabsTrigger value="nurse-procedure" className="flex items-center space-x-2">
                <Activity className="h-4 w-4" />
                <span>Nurse-Procedure Assistance</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="nurse-doctor" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Nurse-Doctor Collaboration</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(
                    filteredNurseDoctorStats,
                    `nurse-doctor-report-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`,
                    ['nurseName', 'doctorName', 'count']
                  )}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export CSV</span>
                </Button>
              </div>

              {/* Search bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search nurse or doctor name..."
                    value={nurseDoctorSearch}
                    onChange={(e) => setNurseDoctorSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseDoctorSort('nurseName')}
                      >
                        <div className="flex items-center space-x-2">
                          <span>Nurse Name</span>
                          {getSortIcon('nurseName', nurseDoctorSort)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseDoctorSort('doctorName')}
                      >
                        <div className="flex items-center space-x-2">
                          <span>Doctor Name</span>
                          {getSortIcon('doctorName', nurseDoctorSort)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseDoctorSort('count')}
                      >
                        <div className="flex items-center justify-end space-x-2">
                          <span>Times Assisted</span>
                          {getSortIcon('count', nurseDoctorSort)}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNurseDoctorStats.length > 0 ? (
                      filteredNurseDoctorStats.map((stat, index) => (
                        <TableRow 
                          key={`${stat.nurseId}-${stat.doctorId}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleNurseDoctorClick(stat)}
                        >
                          <TableCell className="font-medium">{stat.nurseName}</TableCell>
                          <TableCell>{stat.doctorName}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{stat.count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No collaborative appointments found for the selected date range.
                          <div className="text-xs mt-1">
                            Looking for appointment-type schedules with both doctor and staff assigned.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="nurse-procedure" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Nurse-Procedure Assistance</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(
                    filteredNurseProcedureStats,
                    `nurse-procedure-report-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`,
                    ['nurseName', 'procedure', 'count']
                  )}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export CSV</span>
                </Button>
              </div>

              {/* Search bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search nurse name or procedure..."
                    value={nurseProcedureSearch}
                    onChange={(e) => setNurseProcedureSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseProcedureSort('nurseName')}
                      >
                        <div className="flex items-center space-x-2">
                          <span>Nurse Name</span>
                          {getSortIcon('nurseName', nurseProcedureSort)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseProcedureSort('procedure')}
                      >
                        <div className="flex items-center space-x-2">
                          <span>Procedure Type</span>
                          {getSortIcon('procedure', nurseProcedureSort)}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleNurseProcedureSort('count')}
                      >
                        <div className="flex items-center justify-end space-x-2">
                          <span>Times Assisted</span>
                          {getSortIcon('count', nurseProcedureSort)}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNurseProcedureStats.length > 0 ? (
                      filteredNurseProcedureStats.map((stat, index) => (
                        <TableRow 
                          key={`${stat.nurseId}-${stat.procedure}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleNurseProcedureClick(stat)}
                        >
                          <TableCell className="font-medium">{stat.nurseName}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{stat.procedure}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{stat.count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No nurse-procedure assistance data found for the selected date range.
                          <div className="text-xs mt-1">
                            Looking for appointment-type schedules with both doctor and staff assigned.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              {modalData?.type === 'nurse-doctor' ? (
                <>
                  <Users className="h-5 w-5" />
                  <span>
                    {modalData.nurseName} assisting Dr {modalData.doctorName}
                  </span>
                </>
              ) : (
                <>
                  <Activity className="h-5 w-5" />
                  <span>
                    {modalData?.nurseName} assisting with {modalData?.procedure}
                  </span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {detailedInstances.length > 0 ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    {detailedInstances.length} instance(s) found in selected date range
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const headers = modalData?.type === 'nurse-doctor' 
                        ? ['date', 'starttime', 'endtime', 'procedure', 'room']
                        : ['date', 'starttime', 'endtime', 'doctor', 'room'];
                      const data = detailedInstances.map(instance => ({
                        date: format(new Date(instance.date), 'MMM d, yyyy'),
                        starttime: instance.startTime,
                        endtime: instance.endTime,
                        procedure: instance.title,
                        doctor: instance.doctorName || '',
                        room: instance.roomName || '',
                      }));
                      exportToCSV(
                        data,
                        `${modalData?.type === 'nurse-doctor' ? 'nurse-doctor' : 'nurse-procedure'}-details-${modalData?.nurseName?.replace(/\s+/g, '-')}-${Date.now()}.csv`,
                        headers
                      );
                    }}
                    className="flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Export Details</span>
                  </Button>
                </div>

                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        {modalData?.type === 'nurse-doctor' ? (
                          <TableHead>Procedure</TableHead>
                        ) : (
                          <TableHead>Doctor</TableHead>
                        )}
                        <TableHead>Room</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailedInstances.map((instance) => (
                        <TableRow key={instance.id}>
                          <TableCell className="font-medium">
                            {format(new Date(instance.date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>{instance.startTime} - {instance.endTime}</span>
                            </div>
                          </TableCell>
                          {modalData?.type === 'nurse-doctor' ? (
                            <TableCell>
                              <Badge variant="outline">{instance.title}</Badge>
                            </TableCell>
                          ) : (
                            <TableCell>
                              {instance.doctorName ? (
                                <span>Dr {instance.doctorName}</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {instance.roomName ? (
                              <div className="flex items-center space-x-1">
                                <MapPin className="h-3 w-3" />
                                <span>{instance.roomName}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No detailed instances found for the selected criteria.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NurseDoctorProcedureReport;
